import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  LayoutDashboard,
  CalendarCheck,
  Users,
  Palette,
  FileText,
  LogOut,
  Eye,
  Menu,
  Clock,
} from "lucide-react";
import { signOut } from "@/lib/admin-auth";
import { supabase } from "@/lib/supabase";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/bookings", icon: CalendarCheck, label: "Bookings" },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/cms", icon: Palette, label: "CMS" },
  { to: "/admin/documents", icon: FileText, label: "Documents" },
  { to: "/admin/activity", icon: Clock, label: "Activity Log" },
];

const ROUTE_LABELS: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/bookings": "Bookings",
  "/admin/users": "Users",
  "/admin/cms": "CMS Settings",
  "/admin/documents": "Documents",
  "/admin/activity": "Activity Log",
};

function getBreadcrumbs(pathname: string) {
  const crumbs: { label: string; to?: string }[] = [];

  if (pathname === "/admin") {
    crumbs.push({ label: "Dashboard" });
    return crumbs;
  }

  crumbs.push({ label: "Dashboard", to: "/admin" });

  const segments = pathname.replace(/^\/admin\/?/, "").split("/").filter(Boolean);
  let built = "/admin";

  for (let i = 0; i < segments.length; i++) {
    built += `/${segments[i]}`;
    const isLast = i === segments.length - 1;

    if (ROUTE_LABELS[built]) {
      crumbs.push(isLast ? { label: ROUTE_LABELS[built] } : { label: ROUTE_LABELS[built], to: built });
    } else if (segments[i - 1] === "documents") {
      crumbs.push({ label: "Edit Document" });
    } else {
      crumbs.push(isLast ? { label: segments[i] } : { label: segments[i], to: built });
    }
  }

  return crumbs;
}

function UserAvatar({ email, size = "md" }: { email: string; size?: "sm" | "md" }) {
  const letter = email.charAt(0).toUpperCase();
  const dim = size === "sm" ? "h-8 w-8 text-xs" : "h-9 w-9 text-sm";
  return (
    <div
      className={`${dim} rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center shrink-0`}
    >
      {letter}
    </div>
  );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex-1 px-3 py-2 space-y-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-primary text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            }`
          }
        >
          <item.icon className="w-4 h-4 shrink-0" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="px-3 py-3 border-t space-y-1">
      <Button
        size="sm"
        variant="ghost"
        className="w-full justify-start gap-2.5 text-gray-600 hover:text-gray-900"
        asChild
      >
        <a href="/" target="_blank" rel="noopener">
          <Eye className="w-4 h-4" /> View Site
        </a>
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="w-full justify-start gap-2.5 text-red-600 hover:text-red-700 hover:bg-red-50"
        onClick={onLogout}
      >
        <LogOut className="w-4 h-4" /> Logout
      </Button>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? null);
    });
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate("/admin/login");
  };

  const breadcrumbs = getBreadcrumbs(location.pathname);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-white border-r flex-col shrink-0">
        <div className="px-4 py-5 border-b">
          <div className="flex items-center gap-3">
            {userEmail && <UserAvatar email={userEmail} />}
            <div className="min-w-0">
              <h1 className="font-bold text-base text-gray-900 leading-tight">Vita Dom</h1>
              {userEmail ? (
                <p className="text-xs text-gray-500 truncate" title={userEmail}>
                  {userEmail}
                </p>
              ) : (
                <p className="text-xs text-gray-400">Admin Panel</p>
              )}
            </div>
          </div>
        </div>

        <SidebarNav />
        <SidebarFooter onLogout={handleLogout} />
      </aside>

      {/* Mobile top bar + sheet */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="px-4 py-5 border-b">
                  <div className="flex items-center gap-3">
                    {userEmail && <UserAvatar email={userEmail} />}
                    <div className="min-w-0">
                      <h2 className="font-bold text-base text-gray-900 leading-tight">Vita Dom</h2>
                      {userEmail ? (
                        <p className="text-xs text-gray-500 truncate" title={userEmail}>
                          {userEmail}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400">Admin Panel</p>
                      )}
                    </div>
                  </div>
                </div>

                <SidebarNav onNavigate={() => setSheetOpen(false)} />
                <SidebarFooter onLogout={handleLogout} />
              </SheetContent>
            </Sheet>
            <span className="font-semibold text-gray-900">Vita Dom Admin</span>
          </div>

          {userEmail && <UserAvatar email={userEmail} size="sm" />}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Breadcrumbs */}
        <div className="bg-white border-b px-4 md:px-6 py-3 mt-14 md:mt-0">
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, i) => (
                <BreadcrumbItem key={i}>
                  {i > 0 && <BreadcrumbSeparator />}
                  {crumb.to ? (
                    <BreadcrumbLink asChild>
                      <NavLink to={crumb.to}>{crumb.label}</NavLink>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  )}
                </BreadcrumbItem>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
